import AppTable from "components/AppTable/AppTable";
import Pagination from "components/Pagination/Pagination";
import { Ticket } from "interfaces/entities/ticket.model";
import { toDate } from "helpers/dateHelper";
import { useTranslation } from "react-i18next";
import { getFullName } from "helpers/utils";
import { Avatar, Box, Chip, Paper } from "@mui/material";
import { FaInbox } from "react-icons/fa";

interface TicketsTableViewProps {
  tickets: Ticket[];
  total: number;
  page: number;
  limit: number;
  onTicketClick: (ticket: Ticket) => void;
  onPageChange: (page: number) => void;
}

type ChipColor = "default" | "info" | "warning" | "success" | "error" | "primary" | "secondary";
type ChipVariant = "filled" | "outlined";

const STATUS_STYLE: Record<string, { color: ChipColor; variant: ChipVariant }> = {
  NEW: { color: "info", variant: "filled" },
  IN_PROGRESS: { color: "warning", variant: "filled" },
  WAITING_FOR_RESPONSE: { color: "warning", variant: "outlined" },
  RESOLVED: { color: "success", variant: "filled" },
  CLOSED: { color: "default", variant: "outlined" },
};

const PRIORITY_STYLE: Record<string, ChipColor> = {
  HIGH: "error",
  MEDIUM: "warning",
  LOW: "info",
};

// Простой детерминированный цвет аватара по строке — чтобы у одного человека всегда
// был один и тот же оттенок, а в списке цвета визуально различались.
const AVATAR_PALETTE = [
  { bg: "#E0E7FF", fg: "#3730A3" },
  { bg: "#FCE7F3", fg: "#9D174D" },
  { bg: "#D1FAE5", fg: "#065F46" },
  { bg: "#FEF3C7", fg: "#92400E" },
  { bg: "#DBEAFE", fg: "#1E40AF" },
  { bg: "#EDE9FE", fg: "#5B21B6" },
  { bg: "#FEE2E2", fg: "#991B1B" },
];

const pickAvatarColor = (seed: string) => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

const Empty = () => <span className="text-gray-400">—</span>;

const PersonCell = ({ name }: { name: string }) => {
  if (!name) return <Empty />;
  const initial = (name[0] || "?").toUpperCase();
  const { bg, fg } = pickAvatarColor(name);
  return (
    <div className="flex items-center gap-1 min-w-0">
      <Avatar
        sx={{
          width: 20,
          height: 20,
          fontSize: 10,
          fontWeight: 600,
          bgcolor: bg,
          color: fg,
        }}
      >
        {initial}
      </Avatar>
      <span className="text-xs truncate">{name}</span>
    </div>
  );
};

export const TicketsTableView = ({
  tickets,
  total,
  page,
  limit,
  onTicketClick,
  onPageChange,
}: TicketsTableViewProps) => {
  const { t } = useTranslation();

  const columnConfig = [
    {
      key: "subject",
      label: "Тема",
      render: (item: any) => (
        <div className="flex items-baseline gap-1 min-w-0">
          <span className="font-medium text-xs truncate">{item.subject || "Без темы"}</span>
          <span className="text-xs text-gray-400 whitespace-nowrap">#{item.id}</span>
        </div>
      ),
    },
    {
      key: "status",
      label: "Статус",
      render: (item: any) => {
        if (!item.status) return <Empty />;
        const style = STATUS_STYLE[item.status] || { color: "default", variant: "filled" };
        return (
          <Chip
            size="small"
            label={t(`ticket.status.${item.status}`)}
            color={style.color}
            variant={style.variant}
            sx={{ fontWeight: 500, height: 20, fontSize: 11, "& .MuiChip-label": { px: 0.75 } }}
          />
        );
      },
    },
    {
      key: "priority",
      label: "Приоритет",
      render: (item: any) => {
        if (!item.priority) return <Empty />;
        return (
          <Chip
            size="small"
            label={t(`priority.${item.priority}`)}
            color={PRIORITY_STYLE[item.priority] || "default"}
            variant="outlined"
            sx={{ height: 20, fontSize: 11, "& .MuiChip-label": { px: 0.75 } }}
          />
        );
      },
    },
    {
      key: "type",
      label: "Тип",
      render: (item: any) =>
        item.type ? <span className="text-xs">{t(`ticket.type.${item.type}`)}</span> : <Empty />,
    },
    {
      key: "category",
      label: "Тематика",
      render: (item: any) =>
        item.category?.name ? <span className="text-xs">{item.category.name}</span> : <Empty />,
    },
    {
      key: "branch",
      label: "Филиал",
      render: (item: any) =>
        item.branch?.name ? <span className="text-xs">{item.branch.name}</span> : <Empty />,
    },
    {
      key: "data.trainNumber",
      label: "Поезд",
      render: (item: any) => {
        const trainNumber = (item as any)?.data?.trainNumber;
        if (!trainNumber) return <Empty />;
        return (
          <span className="text-xs font-mono px-1 rounded bg-gray-100 text-gray-700">
            #{String(trainNumber).slice(0, 8)}
          </span>
        );
      },
    },
    {
      key: "customer",
      label: "Абонент",
      render: (item: any) => <PersonCell name={item?.customer ? getFullName(item.customer) : ""} />,
    },
    {
      key: "member",
      label: "Отвественный",
      render: (item: any) => <PersonCell name={item?.member?.name || ""} />,
    },
    {
      key: "createdAt",
      label: "Создано",
      render: (item: any) => (
        <span className="text-xs text-gray-600 whitespace-nowrap">{toDate(item.createdAt)}</span>
      ),
    },
  ];

  if (tickets.length === 0) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 6,
          borderRadius: 2,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
        }}
      >
        <FaInbox size={36} className="text-gray-300" />
        <div className="text-sm text-gray-600 font-medium">Обращения не найдены</div>
        <div className="text-xs text-gray-400">Попробуйте изменить фильтры или критерии поиска</div>
      </Paper>
    );
  }

  return (
    <div>
      <Box
        sx={{
          "& .MuiTableCell-root": {
            padding: "4px 6px !important",
            fontSize: "0.75rem",
            lineHeight: 1.3,
          },
          "& .MuiTableCell-head": {
            fontWeight: 600,
            color: "#475569",
            backgroundColor: "#F8FAFC",
            whiteSpace: "nowrap",
          },
        }}
      >
        <AppTable
          data={tickets}
          columnConfig={columnConfig as any}
          rowConfig={
            ((item: any) => ({
              onClick: () => onTicketClick(item),
              sx: {
                cursor: "pointer",
                transition: "background-color 0.15s",
                "&:hover": { backgroundColor: "rgba(99, 102, 241, 0.06)" },
              },
            })) as any
          }
        />
      </Box>
      {total > limit && (
        <div className="mt-4">
          <Pagination total={total} limit={limit} page={page} onPageChange={onPageChange} />
        </div>
      )}
    </div>
  );
};
